import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { Parser } from 'xml2js';
import { SessionQuery } from '@state/session/session.query';
import { catchError, switchMap } from 'rxjs/operators';
import { AesRequestModel } from '@core/models/AesRequest.model';
import { SessionService } from '@state/session/session.service';
import { ERROR_CALL_DATA, ERROR_CALL_MAPPER, ERROR_CODES } from '@core/constants';
import { NavController } from '@ionic/angular';
import { ToastService } from '@core/services/toast/toast.service';
import { LibraryService } from '@core/services/library/library.service';
import { ModalService } from '@core/services/modal/modal.service';
import { INF_SYSTEM_RELOAD } from '@core/infs';
import { AgendaService } from '@core/state/agenda/agenda.service';
import { AgendaStore, DOUBLE_VISUAL_AGENDA } from '@core/state/agenda/agenda.store';
import { AgendaQuery } from '@core/state/agenda/agenda.query';

@Injectable()
export class DataInterceptor implements HttpInterceptor {

    private controlledError: boolean;

    constructor(
        private agendaService: AgendaService,
        private agendaQuery: AgendaQuery,
        private agendaStore: AgendaStore,
        private libraryService: LibraryService,
        private modalService: ModalService,
        private navController: NavController,
        private sessionQuery: SessionQuery,
        private sessionService: SessionService,
        private toastService: ToastService) {
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const { aesUrl, sessionId } = this.sessionQuery.getValue();

        if (!aesUrl || req.responseType !== 'text') {
            return next.handle(req).pipe(
                catchError(err => {
                    throw err;
                })
            );
        }

        const request: HttpRequest<any> = req.clone({
            headers: new HttpHeaders({
                'Content-Type': 'application/x-www-form-urlencoded',
                'Fuck-The-Cookie': sessionId
            }),
            body: (req.body as AesRequestModel).toString()
        });

        return next.handle(request).pipe(
            switchMap(async (event: HttpResponse<any>) => {
                this.controlledError = false;

                if (event instanceof HttpResponse && event.body) {
                    const responseBody = this.xmlToJson(event.body);

                    this.sessionService.updateSessionId(event.headers.get('Fuck-The-Cookie'));

                    // Verificar si el parsing fue exitoso antes de continuar
                    if (responseBody && responseBody.FLOWww_AES && responseBody.FLOWww_AES.FLOWww_Info) {
                        const errorId = Number(responseBody.FLOWww_AES.FLOWww_Info.error);

                        if (errorId !== ERROR_CODES.ERROR_NONE && errorId !== ERROR_CODES.ERROR_OTP) {
                            let errVar = null;
                            const currentData = responseBody.FLOWww_AES.FLOWww_Info?.FLOWww_Data;
                            if(currentData){
                                if (Array.isArray(currentData)) {
                                    errVar = currentData.find((data)=>{
                                        return data.name === 'ErrVar1';
                                    })?.value;
                                } else {
                                    if(currentData.name === 'ErrVar1'){
                                        errVar = currentData.value
                                    }
                                }
                            }

                            await this.handleError(errorId, errVar);
                        }
                    } else {
                        // console.error('Error parsing XML response - responseBody structure:', responseBody);
                        this.presentCommonErrorToast();
                        // Retornar respuesta vacía en lugar de continuar con datos corruptos
                        return event.clone({
                            body: JSON.stringify({ error: 'XML_PARSE_ERROR', data: {} })
                        });
                    }

                    // Solo continuar si tenemos una estructura válida
                    if (responseBody.FLOWww_AES && responseBody.FLOWww_AES.FLOWww_Info) {
                        responseBody.inf = responseBody.FLOWww_AES.FLOWww_Info.inf;
                        responseBody.error = responseBody.FLOWww_AES.FLOWww_Info.error;
                        responseBody.version = responseBody.FLOWww_AES.FLOWww_Info.version;
                        responseBody.data = { };

                        if (responseBody.inf === INF_SYSTEM_RELOAD) {
                            const infLabel = this.libraryService.getLabel('lbl_system_reload_clinic');
                            this.toastService.presentErrorToast(infLabel);
                            await this.sessionService.systemReload();
                            throw 'system_reload';
                        }

                        /**
                         * Build a json object with xml data elements parsed
                         * Transforms FLOWww_Data XML objects into key: value properties
                         */
                        if (responseBody.FLOWww_AES.FLOWww_Info.FLOWww_Data) {
                            if (Array.isArray(responseBody.FLOWww_AES.FLOWww_Info.FLOWww_Data)) {
                                responseBody.FLOWww_AES.FLOWww_Info.FLOWww_Data
                                    .forEach(item => this.mapXMLDataToJson(item, responseBody));
                            } else {
                                const dataTag = responseBody.FLOWww_AES.FLOWww_Info.FLOWww_Data;
                                this.mapXMLDataToJson(dataTag, responseBody);
                            }
                        }

                        /**
                         * Build json objects with xml lists
                         * Transforms FLOWww_List and FLOWww_Item XML objects
                         * into json array and adds it to new __list json property
                         */
                        if (responseBody.FLOWww_AES.FLOWww_Info.FLOWww_List) {
                            if (Array.isArray(responseBody.FLOWww_AES.FLOWww_Info.FLOWww_List)) {
                                responseBody.FLOWww_AES.FLOWww_Info.FLOWww_List
                                    .map(this.mapXMLListToJson)
                                    .forEach(({ name, items }, index) => {
                                        // If list has a name, also save it with that name into response object
                                        if (name) {
                                            responseBody.data[name] = items;
                                        }

                                        responseBody.data[`__list_${index}`] = items;
                                    });
                            } else {
                                const { name, items } = this.mapXMLListToJson(responseBody.FLOWww_AES.FLOWww_Info.FLOWww_List);
                                // If list has a name, also save it with that name into response object
                                if (name) {
                                    responseBody.data[name] = items;
                                }

                                responseBody.data.__list_0 = items;
                            }
                        }
                    }

                    event = event.clone({
                        body: JSON.stringify(responseBody)
                    });
                } else {
                    this.libraryService.getLabelAsync('lbl_common_error').toPromise().then(label => {
                        this.toastService.presentErrorToast(label +  ' ' + ERROR_CALL_DATA);
                    });

                    return;
                }

                return event;
            }),
            catchError(err => {
                if (!this.controlledError) {
                    this.presentCommonErrorToast();
                }

                return throwError(err);
            })
        );
    }


    private xmlToJson(xml: string): any {
        const xmlParser = new Parser({ explicitArray: false, ignoreAttrs: false, mergeAttrs: true });
        let json = { };

        try {
            xmlParser.parseString(xml, (err, result) => {
                if (err) {
                    // console.error('XML parsing error:', err);
                    json = null;
                } else {
                    json = result;
                }
            });
        } catch (error) {
            // console.error('XML parsing exception:', error);
            json = null;
        }

        return json;
    }

    private async handleError(errorId: number, errVar: string): Promise<void> {
        this.controlledError = true;
        let errorLabel: string;
        switch (errorId) {
            case ERROR_CODES.ERROR_SESSION_EXPIRED:
                errorLabel = this.libraryService.getLabel('lbl_expired_session');
                break;

            case ERROR_CODES.ERROR_SESSION_SPOOFED:
            case ERROR_CODES.ERROR_SESSION_MULTIPLE:
                errorLabel = this.libraryService.getLabel('lbl_session_multiple');
                break;

            case ERROR_CODES.ERROR_SYSTEM_TRUNCATED:
                errorLabel = this.libraryService.getLabel('lbl_updated_configuration');
                break;

            case ERROR_CODES.ERROR_LOGIN:
                errorLabel = this.libraryService.getLabel('lbl_login_error');
                break;

            case ERROR_CODES.ERROR_CLINICS_NOT_AVAILABLE:
                errorLabel = this.libraryService.getLabel('596');
                break;

            // Manual handle of user overlapped error
            // No muestra error porque luego se decide en código si mostrar o no
            case ERROR_CODES.ERROR_USER_OVERLAPPED:
                throw errorId;
                break;

            case ERROR_CODES.ERROR_OTP:
                break;
            case ERROR_CODES.ERROR_PROFESSIONAL_VISUAL:
                errorLabel = this.libraryService.getLabel(errorId.toString()).replace('###1###', errVar);
                this.toastService.presentErrorToast(errorLabel);
                this.agendaService.setDoubleDisplay(DOUBLE_VISUAL_AGENDA.CABINETS);
                this.agendaStore.update({hasDoubleVisual: false});
                this.agendaService.getDiaryByDate(this.agendaQuery.getValue().currentMoment).subscribe();
                throw errorId;
                break;
            case ERROR_CODES.ERROR_RIGHTS_PERM_STOPPED:
                errorLabel = this.libraryService.getLabel('372');
                break;

            default:
                errorLabel = this.libraryService.getLabel(errorId.toString()).replace('###1###', errVar);
                this.toastService.presentErrorToast(errorLabel);
                throw errorId;
        }

        // Logout and present error
        await this.sessionService.systemLogout();

        this.toastService.presentErrorToast(errorLabel);
        throw errorId;
    }

    private async presentCommonErrorToast(): Promise<void> {
        const undefinedErrorLabel: string = await this.libraryService.getLabelAsync('lbl_common_error').toPromise();

        this.toastService.presentErrorToast(undefinedErrorLabel);
    }

    private mapXMLDataToJson(item, responseBody): void {
        if (Object.keys(item).length > 2) {
            const newObject = { };

            for (const itemKey of Object.keys(item).filter(i => i !== 'name')) {
                newObject[itemKey] = item[itemKey];
            }

            responseBody.data[item.name] = newObject;
        } else {
            responseBody.data[item.name] = item.value;
        }
    }

    private mapXMLListToJson({ name, FLOWww_Item }): { name: string; items: any[] } {
        const items = [];
        const mapProps = (item: any) => {
            const props = { };
            for (const prop in item) {
                if (item.hasOwnProperty(prop)) {
                    props[prop] = item[prop];
                }
            }
            return props;
        };

        if (FLOWww_Item) {
            if (Array.isArray(FLOWww_Item)) {
                FLOWww_Item.forEach((item => {
                    items.push(mapProps(item));
                }));
            } else {
                items.push(mapProps(FLOWww_Item));
            }
        }

        return { name, items };
    }
}
